import {Observable} from 'rxjs';
import {ApolloLink} from '@apollo/client/core';
import {CombinedGraphQLErrors} from '@apollo/client/errors';
import {ErrorLink} from '@apollo/client/link/error';
import {isUnauthorized} from '../auth-links';

/**
 * The app-wide safety net for GraphQL calls (VTK-3582): a request that never reached the API (offline,
 * DNS, 5xx, CORS) is reported to the user once, whatever the call site does. GraphQL errors (the API
 * answered and refused) are left to the call site, which knows what failed; most already show them.
 * A 401 is not reported here -- retryAfterRefreshLink refreshes the login or starts a new one.
 *
 * `notify` receives one message per distinct failure while that message is still shown.
 */
export function networkErrorLink(notify: (message: string) => void): ApolloLink {
  return new ErrorLink(({error, operation}) => {
    if (CombinedGraphQLErrors.is(error) || isUnauthorized(error)) {
      return;
    }
    console.error(`GraphQL ${operation.operationName ?? 'operation'} failed`, error);
    notify(`Could not reach the server: ${errorText(error)}`);
  });
}

/** A short, human-readable text for any error a GraphQL call can produce. */
export function errorText(error: unknown): string {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.map(e => e.message).join('; ') || 'The request was refused.';
  }
  const e = error as {message?: string, status?: number, statusCode?: number} | null;
  const status = e?.status ?? e?.statusCode;
  const message = e?.message || (typeof error === 'string' ? error : '') || 'Unknown error.';
  return status ? `${message} (HTTP ${status})` : message;
}

/**
 * Shows each distinct message once while it is on screen: a burst of failing queries (a page with ten
 * calls, a poll) gives one banner, not ten queued ones.
 */
export function onceWhileShown(open: (message: string) => Observable<unknown>): (message: string) => void {
  const shown = new Set<string>();
  return message => {
    if (shown.has(message)) return;
    shown.add(message);
    // Released however the banner ends -- answered, its outlet destroyed, or never shown (no outlet,
    // e.g. on /reports): a key kept for a banner nobody sees would hide that error for good.
    const release = () => shown.delete(message);
    open(message).subscribe({next: release, complete: release, error: release});
  };
}
